import { useState, useEffect, useRef } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter 
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { 
  Plus, 
  Edit2, 
  Trash2, 
  GripVertical,
  FolderPlus,
  Image,
  Upload,
  MoreVertical,
  Pencil,
  AlertTriangle,
  Leaf,
  DollarSign,
  X,
  Lock,
  Unlock,
  Wallet,
  Percent,
  Euro,
  Copy,
  RotateCcw,
  RotateCw,
  Loader2,
  Tag
} from 'lucide-react';
import axios from 'axios';
import { SetPinModal, VerifyPinModal, LockBadge } from '../../components/PinPad';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getFullImageUrl } from '../../utils/images';
import { getApiErrorMessage } from '../../utils/errorHelper';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sortable Category Item Component
function SortableCategoryItem({ category, isSelected, onSelect, onEdit, onDelete, onSetPin, onToggleLock, onToggleHighlight, isAccessible, dishCount }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 p-3 rounded-xl cursor-pointer transition-all ${
        isSelected
          ? 'bg-emerald-50 border-2 border-emerald-500'
          : category.is_locked && !isAccessible
            ? 'bg-amber-50 border border-amber-200 hover:border-amber-300'
            : 'bg-white border border-gray-200 hover:border-emerald-300'
      }`}
      onClick={() => onSelect(category.id)}
    >
      <button
        {...attributes}
        {...listeners}
        className="p-1 cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
        onClick={(e) => e.stopPropagation()}
      >
        <GripVertical className="w-4 h-4" />
      </button>
      
      {/* Lock indicator */}
      {category.highlight && (
        <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-violet-100 text-violet-700">Diff.</span>
      )}
      {category.has_pin && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleLock(category);
          }}
          className={`p-1 rounded-full transition-colors ${
            category.is_locked 
              ? 'text-amber-600 bg-amber-100 hover:bg-amber-200' 
              : 'text-emerald-600 bg-emerald-100 hover:bg-emerald-200'
          }`}
          title={category.is_locked ? 'Déverrouiller' : 'Verrouiller'}
        >
          {category.is_locked ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
        </button>
      )}
      
      <span className="flex-1 font-medium truncate">{category.name}</span>
      <Badge variant="secondary" className="text-xs">{dishCount}</Badge>
      <DropdownMenu>
        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
          <button className="p-1 hover:bg-gray-100 rounded">
            <MoreVertical className="w-4 h-4 text-gray-400" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => onEdit(category)}>
            <Pencil className="w-4 h-4 mr-2" /> Renommer
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onToggleHighlight(category)}>
            <Tag className="w-4 h-4 mr-2" />
            {category.highlight ? 'Ne plus differencier' : 'Differencier'}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onSetPin(category)}>
            <Lock className="w-4 h-4 mr-2" /> 
            {category.has_pin ? 'Modifier le PIN' : 'Définir un PIN'}
          </DropdownMenuItem>
          {category.has_pin && (
            <DropdownMenuItem onClick={() => onToggleLock(category)}>
              {category.is_locked ? (
                <><Unlock className="w-4 h-4 mr-2" /> Déverrouiller</>
              ) : (
                <><Lock className="w-4 h-4 mr-2" /> Verrouiller</>
              )}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem 
            onClick={() => onDelete(category.id)}
            className="text-red-600"
          >
            <Trash2 className="w-4 h-4 mr-2" /> Supprimer
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

// Sortable Dish Item Component
function SortableDishItem({ dish, onEdit, onDelete, onDuplicate, onToggleAvailability, onUpdateStock }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: dish.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  
  const isLowStock = dish.stock !== null && dish.stock !== undefined && dish.stock <= (dish.low_stock_threshold || 5);
  const isOutOfStock = dish.stock !== null && dish.stock !== undefined && dish.stock === 0;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-3 bg-white rounded-xl border ${isOutOfStock || !dish.is_available ? 'border-red-200 bg-red-50' : isLowStock ? 'border-orange-200 bg-orange-50' : 'border-gray-200'} ${isDragging ? 'shadow-lg' : ''}`}
    >
      <button
        {...attributes}
        {...listeners}
        className="p-1 cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
      >
        <GripVertical className="w-4 h-4" />
      </button>
      
      {dish.image ? (
        <img src={getFullImageUrl(dish.image)} alt="" className={`w-12 h-12 rounded-lg object-cover ${!dish.is_available ? 'opacity-50 grayscale' : ''}`} />
      ) : (
        <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center">
          <Image className="w-5 h-5 text-gray-400" />
        </div>
      )}
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className={`font-medium truncate ${!dish.is_available ? 'text-gray-400 line-through' : ''}`}>{dish.name}</p>
          {isOutOfStock && (
            <Badge className="bg-red-500 text-white text-xs">Épuisé</Badge>
          )}
          {isLowStock && !isOutOfStock && (
            <Badge className="bg-orange-500 text-white text-xs">Stock bas</Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <p className={`text-sm font-semibold ${!dish.is_available ? 'text-gray-400' : 'text-emerald-600'}`}>{dish.price?.toFixed(2)} €</p>
          <span className="text-xs text-gray-400">TVA {dish.vat_rate || 10}%</span>
        </div>
      </div>
      
      {/* Stock Management */}
      <div className="flex items-center gap-1">
        <input
          type="number"
          min="0"
          value={dish.stock ?? ''}
          placeholder="∞"
          onChange={(e) => {
            const val = e.target.value === '' ? null : parseInt(e.target.value);
            onUpdateStock(dish.id, val);
          }}
          className="w-14 h-7 text-xs text-center border border-gray-200 rounded-md focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
          title="Quantité en stock (vide = illimité)"
        />
      </div>
      
      <div className="flex items-center gap-2">
        {dish.allergens?.length > 0 && (
          <Badge variant="outline" className="text-xs text-orange-600 border-orange-200 hidden sm:flex">
            <AlertTriangle className="w-3 h-3 mr-1" />
            {dish.allergens.length}
          </Badge>
        )}
        {dish.ingredients?.enabled && (
          <Badge variant="outline" className="text-xs text-emerald-600 border-emerald-200 hidden sm:flex">
            <Leaf className="w-3 h-3 mr-1" />
          </Badge>
        )}
        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-500 hidden sm:inline">{dish.is_available ? 'Dispo' : 'Épuisé'}</span>
          <Switch
            checked={dish.is_available}
            onCheckedChange={() => onToggleAvailability(dish)}
            className="scale-75"
          />
        </div>
        <button onClick={(e) => { e.stopPropagation(); onDuplicate(dish); }} className="p-1 hover:bg-blue-50 rounded" title="Dupliquer">
          <Copy className="w-4 h-4 text-blue-500" />
        </button>
        <button onClick={(e) => { e.stopPropagation(); onEdit(dish); }} className="p-1 hover:bg-gray-100 rounded">
          <Edit2 className="w-4 h-4 text-gray-500" />
        </button>
        <button onClick={(e) => { e.stopPropagation(); onDelete(dish.id); }} className="p-1 hover:bg-red-50 rounded">
          <Trash2 className="w-4 h-4 text-red-500" />
        </button>
      </div>
    </div>
  );
}

// Sortable Ingredient Category Component (for dish modal)
function SortableIngredientCategory({ category, catIndex, onUpdate, onRemove, onAddItem, onUpdateItem, onRemoveItem, onReorderItems }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleItemDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const items = category.items || [];
      const oldIndex = items.findIndex((i) => i.id === active.id);
      const newIndex = items.findIndex((i) => i.id === over.id);
      const newItems = arrayMove(items, oldIndex, newIndex);
      onReorderItems(catIndex, newItems);
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="p-4 rounded-xl border border-gray-200 bg-white"
    >
      {/* Category Header */}
      <div className="flex items-center gap-2 mb-3">
        <button
          {...attributes}
          {...listeners}
          className="p-1 cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
        >
          <GripVertical className="w-4 h-4" />
        </button>
        <Input
          value={category.name}
          onChange={(e) => onUpdate(catIndex, 'name', e.target.value)}
          placeholder="Nom de la catégorie (ex: Base, Protéines...)"
          className="flex-1 rounded-lg font-medium"
        />
        <button
          onClick={() => onRemove(catIndex)}
          className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {/* Category Settings */}
      <div className="grid grid-cols-3 gap-3 mb-3 p-3 bg-gray-50 rounded-lg">
        <div>
          <Label className="text-xs text-gray-500">Min. requis</Label>
          <Input
            type="number"
            min="0"
            value={category.min_selection}
            onChange={(e) => onUpdate(catIndex, 'min_selection', e.target.value === '' ? '' : parseInt(e.target.value))}
            className="mt-1 rounded-lg text-sm"
          />
          <p className="text-xs text-gray-400 mt-0.5">
            {category.min_selection > 0 ? 'Obligatoire' : 'Optionnel'}
          </p>
        </div>
        <div>
          <Label className="text-xs text-gray-500">Max. choix</Label>
          <Input
            type="number"
            min="1"
            value={category.max_selection}
            onChange={(e) => onUpdate(catIndex, 'max_selection', e.target.value === '' ? '' : parseInt(e.target.value))}
            className="mt-1 rounded-lg text-sm"
          />
          <p className="text-xs text-gray-400 mt-0.5">
            Max {category.max_selection} choix
          </p>
        </div>
        <div>
          <Label className="text-xs text-gray-500">Gratuits</Label>
          <Input
            type="number"
            min="0"
            value={category.free_quantity}
            onChange={(e) => onUpdate(catIndex, 'free_quantity', e.target.value === '' ? '' : parseInt(e.target.value))}
            className="mt-1 rounded-lg text-sm"
          />
          <p className="text-xs text-gray-400 mt-0.5">
            {category.free_quantity > 0 
              ? `${category.free_quantity} gratuit(s)` 
              : 'Tous payants'}
          </p>
        </div>
      </div>

      {/* Category Items */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-gray-500">
            Ingrédients ({category.items?.length || 0})
          </span>
          <Button 
            size="sm" 
            variant="ghost" 
            onClick={() => onAddItem(catIndex)}
            className="h-7 text-xs"
          >
            <Plus className="w-3 h-3 mr-1" />
            Ajouter
          </Button>
        </div>

        <DndContext
          collisionDetection={closestCenter}
          onDragEnd={handleItemDragEnd}
        >
          <SortableContext items={category.items?.map(i => i.id) || []} strategy={verticalListSortingStrategy}>
            {category.items?.map((item, itemIndex) => (
              <SortableIngredientItem
                key={item.id}
                item={item}
                catIndex={catIndex}
                itemIndex={itemIndex}
                onUpdate={onUpdateItem}
                onRemove={onRemoveItem}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
}

// Sortable Ingredient Item Component
function SortableIngredientItem({ item, catIndex, itemIndex, onUpdate, onRemove }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div 
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg"
    >
      <button
        {...attributes}
        {...listeners}
        className="p-0.5 cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
      >
        <GripVertical className="w-3 h-3" />
      </button>
      <Input
        value={item.name}
        onChange={(e) => onUpdate(catIndex, itemIndex, 'name', e.target.value)}
        placeholder="Nom"
        className="flex-1 h-8 rounded-lg text-sm"
      />
      <div className="flex items-center gap-1">
        <DollarSign className="w-3 h-3 text-gray-400" />
        <Input
          type="number"
          step="0.01"
          value={item.price ?? ''}
          onChange={(e) => onUpdate(catIndex, itemIndex, 'price', e.target.value === '' ? '' : parseFloat(e.target.value))}
          placeholder="0"
          className="w-16 h-8 rounded-lg text-sm"
          title="Prix positif = supplément, négatif = réduction"
        />
        <span className="text-xs text-gray-400">€</span>
      </div>
      <label className="flex items-center gap-1 text-xs whitespace-nowrap">
        <Checkbox
          checked={item.is_available === true}
          onCheckedChange={(checked) => onUpdate(catIndex, itemIndex, 'is_available', checked === true)}
        />
        Dispo
      </label>
      <button
        onClick={() => onRemove(catIndex, itemIndex)}
        className="p-1 text-red-400 hover:text-red-600"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}

// Standard allergens (will be fetched from API)
const DEFAULT_ALLERGENS = [
  "Gluten", "Crustacés", "Oeufs", "Poissons", "Arachides", 
  "Soja", "Lait", "Fruits à coque", "Céleri", "Moutarde",
  "Sésame", "Sulfites", "Lupin", "Mollusques"
];

export default function RestaurantMenu() {
  useAuth();
  const [categories, setCategories] = useState([]);
  const [dishes, setDishes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [saving, setSaving] = useState(false);
  const [allergensList, setAllergensList] = useState(DEFAULT_ALLERGENS);
  
  // Modals
  const [categoryModal, setCategoryModal] = useState({ open: false, data: null });
  const [dishModal, setDishModal] = useState({ open: false, data: null });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null });
  const [pinModal, setPinModal] = useState({ open: false, category: null });
  const [verifyPinModal, setVerifyPinModal] = useState({ open: false, category: null, onSuccess: null });
  const [unlockedCategories, setUnlockedCategories] = useState(new Set()); // Track unlocked categories for this session
  
  // File upload refs
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [rotatingDish, setRotatingDish] = useState(false);

  useEffect(() => {
    fetchMenu();
    fetchAllergens();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAllergens = async () => {
    try {
      const response = await axios.get(`${API}/restaurant-dashboard/menu/allergens`);
      setAllergensList(response.data.allergens);
    } catch {
      console.log('Using default allergens');
    }
  };

  const fetchMenu = async () => {
    try {
      const [catRes, dishRes] = await Promise.all([
        axios.get(`${API}/restaurant-dashboard/menu/categories`),
        axios.get(`${API}/restaurant-dashboard/menu/dishes`)
      ]);
      // Sort by position
      const sortedCategories = catRes.data.sort((a, b) => (a.position || 0) - (b.position || 0));
      const sortedDishes = dishRes.data.sort((a, b) => (a.position || 0) - (b.position || 0));
      setCategories(sortedCategories);
      setDishes(sortedDishes);
      if (sortedCategories.length > 0 && !selectedCategory) {
        setSelectedCategory(sortedCategories[0].id);
      }
    } catch {
      toast.error('Erreur lors du chargement du menu');
    } finally {
      setLoading(false);
    }
  };

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Reorder categories handler
  const handleCategoryDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = categories.findIndex((c) => c.id === active.id);
    const newIndex = categories.findIndex((c) => c.id === over.id);
    const newCategories = arrayMove(categories, oldIndex, newIndex);
    
    // Update local state immediately
    setCategories(newCategories);
    
    // Save to backend
    const positions = newCategories.map((c, idx) => ({ id: c.id, position: idx }));
    try {
      await axios.put(`${API}/restaurant-dashboard/menu/categories/reorder`, positions);
      toast.success('Ordre des catégories mis à jour');
    } catch {
      toast.error('Erreur lors de la réorganisation');
      fetchMenu(); // Revert on error
    }
  };

  // Reorder dishes handler
  const handleDishDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const categoryDishes = dishes.filter(d => d.category_id === selectedCategory);
    const oldIndex = categoryDishes.findIndex((d) => d.id === active.id);
    const newIndex = categoryDishes.findIndex((d) => d.id === over.id);
    const reorderedDishes = arrayMove(categoryDishes, oldIndex, newIndex);
    
    // Update local state
    const otherDishes = dishes.filter(d => d.category_id !== selectedCategory);
    setDishes([...otherDishes, ...reorderedDishes]);
    
    // Save to backend
    const positions = reorderedDishes.map((d, idx) => ({ id: d.id, position: idx }));
    try {
      await axios.put(`${API}/restaurant-dashboard/menu/dishes/reorder`, positions);
      toast.success('Ordre des plats mis à jour');
    } catch {
      toast.error('Erreur lors de la réorganisation');
      fetchMenu();
    }
  };


  // Dish image rotation handler
  const handleRotateDishImage = async (direction) => {
    if (!dishModal.data?.image || rotatingDish) return;
    setRotatingDish(true);
    try {
      const photoUrl = getFullImageUrl(dishModal.data.image);
      // Fetch image as blob to avoid CORS canvas tainting from browser cache
      const response = await fetch(photoUrl);
      if (!response.ok) throw new Error('Failed to fetch image');
      const imageBlob = await response.blob();
      const objectUrl = URL.createObjectURL(imageBlob);
      const img = new window.Image();
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = objectUrl;
      });
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const angle = direction === 'cw' ? 90 : -90;
      canvas.width = img.height;
      canvas.height = img.width;
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((angle * Math.PI) / 180);
      ctx.drawImage(img, -img.width / 2, -img.height / 2);
      URL.revokeObjectURL(objectUrl);
      const rotatedBlob = await new Promise(resolve => canvas.toBlob(resolve, 'image/webp', 0.9));
      if (!rotatedBlob) throw new Error('Failed to create rotated image');
      const formData = new FormData();
      formData.append('file', rotatedBlob, 'rotated.webp');
      const uploadResponse = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (uploadResponse.data.error) throw new Error(uploadResponse.data.error);
      setDishModal({
        ...dishModal,
        data: { ...dishModal.data, image: uploadResponse.data.url }
      });
      toast.success('Image pivotée');
    } catch (error) {
      console.error('Rotation error:', error);
      toast.error('Erreur lors de la rotation');
    } finally {
      setRotatingDish(false);
    }
  };

  // File upload handler
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format non supporté. Utilisez JPG, PNG, WebP ou GIF.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Fichier trop volumineux. Maximum 5MB.');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setDishModal(prev => ({
        ...prev,
        data: { ...prev.data, image: response.data.url }
      }));
      toast.success('Image uploadée !');
    } catch {
      toast.error("Erreur lors de l'upload de l'image");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Category handlers
  const handleSaveCategory = async () => {
    if (saving) return;
    if (!categoryModal.data?.name?.trim()) {
      toast.error('Veuillez entrer un nom de catégorie');
      return;
    }

    setSaving(true);
    try {
      if (categoryModal.data?.id) {
        await axios.put(`${API}/restaurant-dashboard/menu/categories/${categoryModal.data.id}`, {
          name: categoryModal.data.name.trim(),
          description: categoryModal.data.description || null,
          pin: categoryModal.data.pin || null,
          is_locked: categoryModal.data.is_locked || false,
          online_only: categoryModal.data.online_only || false,
          position: categoryModal.data.position || 0
        });
        toast.success('Catégorie mise à jour');
      } else {
        await axios.post(`${API}/restaurant-dashboard/menu/categories`, {
          name: categoryModal.data.name.trim(),
          description: categoryModal.data.description || null,
          pin: categoryModal.data.pin || null,
          is_locked: categoryModal.data.is_locked || false,
          online_only: categoryModal.data.online_only || false,
          position: categories.length
        });
        toast.success('Catégorie créée');
      }
      setCategoryModal({ open: false, data: null });
      await fetchMenu();
    } catch {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // PIN handlers
  const handleSetPin = async (category) => {
    setPinModal({ open: true, category });
  };

  const handleSavePin = async (pin) => {
    if (!pinModal.category) return;
    
    try {
      await axios.put(`${API}/restaurant-dashboard/menu/categories/${pinModal.category.id}`, {
        name: pinModal.category.name,
        description: pinModal.category.description,
        pin: pin,
        is_locked: pin ? true : false
      });
      
      if (pin) {
        toast.success('PIN défini avec succès');
      } else {
        toast.success('PIN supprimé');
        // Remove from unlocked set
        setUnlockedCategories(prev => {
          const newSet = new Set(prev);
          newSet.delete(pinModal.category.id);
          return newSet;
        });
      }
      setPinModal({ open: false, category: null });
      await fetchMenu();
    } catch {
      toast.error('Erreur lors de la sauvegarde du PIN');
    }
  };

  const handleToggleLock = async (category) => {
    try {
      const newLockState = !category.is_locked;
      await axios.post(`${API}/restaurant-dashboard/menu/categories/${category.id}/unlock`, null, {
        params: { lock: newLockState }
      });
      
      if (newLockState) {
        // Remove from unlocked set when locking
        setUnlockedCategories(prev => {
          const newSet = new Set(prev);
          newSet.delete(category.id);
          return newSet;
        });
      }
      
      toast.success(newLockState ? 'Catégorie verrouillée' : 'Catégorie déverrouillée');
      await fetchMenu();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Erreur lors du changement de verrouillage'));
    }
  };

  const handleVerifyPin = async (pin) => {
    if (!verifyPinModal.category) return { valid: false };
    
    try {
      const response = await axios.post(
        `${API}/restaurant-dashboard/menu/categories/${verifyPinModal.category.id}/verify-pin`,
        null,
        {
          params: { pin }
        }
      );
      
      if (response.data.valid) {
        // Add to unlocked set for this session
        setUnlockedCategories(prev => new Set([...prev, verifyPinModal.category.id]));
        if (verifyPinModal.onSuccess) {
          verifyPinModal.onSuccess();
        }
      }
      
      return response.data;
    } catch {
      return { valid: false, message: 'Erreur de vérification' };
    }
  };

  const isCategoryAccessible = (category) => {
    if (!category.is_locked || !category.has_pin) return true;
    return unlockedCategories.has(category.id);
  };

  const handleCategoryClick = (category, callback) => {
    if (isCategoryAccessible(category)) {
      callback();
    } else {
      setVerifyPinModal({ 
        open: true, 
        category, 
        onSuccess: callback 
      });
    }
  };

  const [deleteCatConfirm, setDeleteCatConfirm] = useState({ open: false, id: null });

  const handleToggleHighlight = async (category) => {
    try {
      const newVal = !category.highlight;
      await axios.put(`${API}/restaurant-dashboard/menu/categories/${category.id}`, {
        highlight: newVal
      });
      setCategories(prev => prev.map(c => c.id === category.id ? { ...c, highlight: newVal } : c));
      toast.success(newVal ? 'Categorie differenciee' : 'Differenciation retiree');
    } catch {
      toast.error('Erreur');
    }
  };

  
  const handleDeleteCategory = async (id) => {
    setDeleteCatConfirm({ open: true, id });
  };
  
  const confirmDeleteCategory = async () => {
    const id = deleteCatConfirm.id;
    setDeleteCatConfirm({ open: false, id: null });
    try {
      await axios.delete(`${API}/restaurant-dashboard/menu/categories/${id}`);
      toast.success('Catégorie supprimée');
      if (selectedCategory === id) {
        setSelectedCategory(categories.find(c => c.id !== id)?.id || null);
      }
      await fetchMenu();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleEditCategory = (category) => {
    setCategoryModal({ 
      open: true, 
      data: { 
        id: category.id, 
        name: category.name, 
        description: category.description || '', 
        position: category.position, 
        has_pin: category.has_pin, 
        is_locked: category.is_locked,
        online_only: category.online_only || false,
        highlight: category.highlight || false
      } 
    });
  };

  // Dish handlers
  const handleSaveDish = async () => {
    if (saving) return;
    
    if (!dishModal.data?.name?.trim()) {
      toast.error('Veuillez entrer un nom de plat');
      return;
    }
    if (!dishModal.data?.price || parseFloat(dishModal.data.price) <= 0) {
      toast.error('Veuillez entrer un prix valide');
      return;
    }
    if (!dishModal.data?.vat_rate) {
      toast.error('Veuillez sélectionner un taux de TVA');
      return;
    }

    setSaving(true);
    try {
      const dishData = {
        category_id: dishModal.data.category_id || selectedCategory,
        name: dishModal.data.name.trim(),
        description: dishModal.data.description?.trim() || null,
        price: parseFloat(dishModal.data.price),
        vat_rate: parseFloat(dishModal.data.vat_rate),
        image: dishModal.data.image || null,
        is_available: dishModal.data.is_available !== false,
        options: dishModal.data.options || [],
        allergens: dishModal.data.allergens || [],
        ingredients: dishModal.data.ingredients || null,
        stock: dishModal.data.stock ?? null,
        low_stock_threshold: dishModal.data.low_stock_threshold === '' || dishModal.data.low_stock_threshold == null ? 5 : dishModal.data.low_stock_threshold,
        // Deposit (acompte) fields
        requires_deposit: dishModal.data.requires_deposit ?? null,
        deposit_type: dishModal.data.deposit_type || null,
        deposit_value: dishModal.data.deposit_value ?? null,
        // Pré-commande
        preorder_only: dishModal.data.preorder_only || false,
        preorder_delay_days: dishModal.data.preorder_only ? (dishModal.data.preorder_delay_days || 1) : 0
      };

      if (dishModal.data?.id) {
        await axios.put(`${API}/restaurant-dashboard/menu/dishes/${dishModal.data.id}`, dishData);
        toast.success('Plat mis à jour');
      } else {
        await axios.post(`${API}/restaurant-dashboard/menu/dishes`, dishData);
        toast.success('Plat créé');
      }
      setDishModal({ open: false, data: null });
      await fetchMenu();
    } catch {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDish = async (id) => {
    setDeleteConfirm({ open: true, id });
  };
  
  const confirmDeleteDish = async () => {
    const id = deleteConfirm.id;
    setDeleteConfirm({ open: false, id: null });
    try {
      await axios.delete(`${API}/restaurant-dashboard/menu/dishes/${id}`);
      toast.success('Plat supprimé');
      await fetchMenu();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDuplicateDish = (dish) => {
    // Open dish modal pre-filled with the duplicated dish data (without id)
    const { id, ...dishData } = dish;
    setDishModal({
      open: true,
      data: {
        ...dishData,
        name: `${dish.name} (copie)`,
      }
    });
  };

  const handleToggleDishAvailability = async (dish) => {
    try {
      await axios.put(`${API}/restaurant-dashboard/menu/dishes/${dish.id}`, {
        is_available: !dish.is_available
      });
      toast.success(dish.is_available ? 'Plat désactivé' : 'Plat activé');
      await fetchMenu();
    } catch {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // Update dish stock
  const handleUpdateStock = async (dishId, stock) => {
    try {
      await axios.put(`${API}/restaurant-dashboard/menu/dishes/${dishId}/stock?stock=${stock === null ? '' : stock}`, {});
      // Update local state immediately for better UX
      setDishes(prev => prev.map(d => 
        d.id === dishId 
          ? { ...d, stock, is_available: stock === 0 ? false : d.is_available }
          : d
      ));
      toast.success('Stock mis à jour');
    } catch {
      toast.error('Erreur lors de la mise à jour du stock');
    }
  };

  // Allergen toggle
  const toggleAllergen = (allergen) => {
    const current = dishModal.data?.allergens || [];
    const updated = current.includes(allergen)
      ? current.filter(a => a !== allergen)
      : [...current, allergen];
    setDishModal(prev => ({
      ...prev,
      data: { ...prev.data, allergens: updated }
    }));
  };

  // Ingredient Category handlers
  const addIngredientCategory = () => {
    const categories = dishModal.data?.ingredients?.categories || [];
    const newCategory = {
      id: `cat-${Date.now()}`,
      name: '',
      min_selection: 0,
      max_selection: 1,
      free_quantity: 0,
      position: categories.length,
      items: []
    };
    setDishModal(prev => ({
      ...prev,
      data: {
        ...prev.data,
        ingredients: {
          enabled: true,
          categories: [...categories, newCategory]
        }
      }
    }));
  };

  const updateIngredientCategory = (catIndex, field, value) => {
    const categories = [...(dishModal.data?.ingredients?.categories || [])];
    categories[catIndex] = { ...categories[catIndex], [field]: value };
    setDishModal(prev => ({
      ...prev,
      data: {
        ...prev.data,
        ingredients: {
          ...prev.data.ingredients,
          categories
        }
      }
    }));
  };

  const removeIngredientCategory = (catIndex) => {
    const categories = [...(dishModal.data?.ingredients?.categories || [])];
    categories.splice(catIndex, 1);
    setDishModal(prev => ({
      ...prev,
      data: {
        ...prev.data,
        ingredients: {
          ...prev.data.ingredients,
          categories
        }
      }
    }));
  };

  const addIngredientItem = (catIndex) => {
    const categories = [...(dishModal.data?.ingredients?.categories || [])];
    const newItem = {
      id: `item-${Date.now()}`,
      name: '',
      is_available: true,
      price: 0
    };
    categories[catIndex].items = [...(categories[catIndex].items || []), newItem];
    setDishModal(prev => ({
      ...prev,
      data: {
        ...prev.data,
        ingredients: {
          ...prev.data.ingredients,
          categories
        }
      }
    }));
  };

  const updateIngredientItem = (catIndex, itemIndex, field, value) => {
    console.log(`Updating ingredient: cat=${catIndex}, item=${itemIndex}, field=${field}, value=${value}`);
    setDishModal(prev => {
      const categories = JSON.parse(JSON.stringify(prev.data?.ingredients?.categories || []));
      if (categories[catIndex] && categories[catIndex].items && categories[catIndex].items[itemIndex]) {
        categories[catIndex].items[itemIndex][field] = value;
      }
      console.log(`Updated categories:`, categories[catIndex]?.items?.[itemIndex]);
      return {
        ...prev,
        data: {
          ...prev.data,
          ingredients: {
            ...prev.data.ingredients,
            categories
          }
        }
      };
    });
  };

  const removeIngredientItem = (catIndex, itemIndex) => {
    const categories = [...(dishModal.data?.ingredients?.categories || [])];
    categories[catIndex].items.splice(itemIndex, 1);
    setDishModal(prev => ({
      ...prev,
      data: {
        ...prev.data,
        ingredients: {
          ...prev.data.ingredients,
          categories
        }
      }
    }));
  };

  // Reorder items within an ingredient category
  const reorderIngredientItems = (catIndex, newItems) => {
    const categories = [...(dishModal.data?.ingredients?.categories || [])];
    categories[catIndex] = { ...categories[catIndex], items: newItems };
    setDishModal(prev => ({
      ...prev,
      data: {
        ...prev.data,
        ingredients: {
          ...prev.data.ingredients,
          categories
        }
      }
    }));
  };

  // Reorder ingredient categories in dish modal
  const handleIngredientCategoryDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const cats = dishModal.data?.ingredients?.categories || [];
    const oldIndex = cats.findIndex((c) => c.id === active.id);
    const newIndex = cats.findIndex((c) => c.id === over.id);
    const newCategories = arrayMove(cats, oldIndex, newIndex).map((c, idx) => ({
      ...c,
      position: idx
    }));

    setDishModal(prev => ({
      ...prev,
      data: {
        ...prev.data,
        ingredients: {
          ...prev.data.ingredients,
          categories: newCategories
        }
      }
    }));
  };

  const filteredDishes = selectedCategory 
    ? (dishes || []).filter(d => d.category_id === selectedCategory)
    : (dishes || []);

  // Open dish modal with default structure
  const openDishModal = (dish = null) => {
    setDishModal({
      open: true,
      data: dish || {
        category_id: selectedCategory,
        name: '',
        description: '',
        price: '',
        vat_rate: '10',  // TVA par défaut 10% pour la restauration sur place
        image: '',
        is_available: true,
        allergens: [],
        ingredients: {
          enabled: false,
          categories: []
        }
      }
    });
  };

  if (loading) {
    return (
      <DashboardLayout title="Menu">
        <div className="animate-pulse space-y-4">
          <div className="h-10 bg-gray-200 rounded-xl w-1/3" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={`skeleton-${i}`} className="h-32 bg-gray-200 rounded-xl" />
            ))}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Menu">
      {/* Categories */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-gray-900">Catégories</h2>
          <Button 
            size="sm" 
            onClick={() => setCategoryModal({ open: true, data: { name: '' } })}
            className="bg-emerald-500 hover:bg-emerald-600 rounded-full"
          >
            <FolderPlus className="w-4 h-4 mr-2" />
            Nouvelle catégorie
          </Button>
        </div>
        
        {categories.length === 0 ? (
          <p className="text-gray-500 text-sm">Aucune catégorie. Créez-en une pour commencer.</p>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleCategoryDragEnd}
          >
            <SortableContext items={(categories || []).map(c => c.id)} strategy={verticalListSortingStrategy}>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {(categories || []).map((cat) => (
                  <SortableCategoryItem
                    key={cat.id}
                    category={cat}
                    isSelected={selectedCategory === cat.id}
                    onSelect={(id) => handleCategoryClick(cat, () => setSelectedCategory(id))}
                    onEdit={(c) => handleCategoryClick(c, () => handleEditCategory(c))}
                    onDelete={handleDeleteCategory}
                    onSetPin={handleSetPin}
                    onToggleLock={handleToggleLock}
                    onToggleHighlight={handleToggleHighlight}
                    isAccessible={isCategoryAccessible(cat)}
                    dishCount={dishes.filter(d => d.category_id === cat.id).length}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      {/* Dishes */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-semibold text-gray-900">
          Plats {selectedCategory && `(${filteredDishes.length})`}
        </h2>
        {selectedCategory && (
          <Button 
            size="sm" 
            onClick={() => openDishModal()}
            className="bg-emerald-500 hover:bg-emerald-600 rounded-full"
          >
            <Plus className="w-4 h-4 mr-2" />
            Ajouter un plat
          </Button>
        )}
      </div>

      {!selectedCategory ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-gray-200">
          <FolderPlus className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Sélectionnez ou créez une catégorie pour gérer les plats</p>
        </div>
      ) : filteredDishes.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-gray-200">
          <Image className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 mb-4">Aucun plat dans cette catégorie</p>
          <Button 
            onClick={() => openDishModal()}
            className="bg-emerald-500 hover:bg-emerald-600 rounded-full"
          >
            <Plus className="w-4 h-4 mr-2" />
            Ajouter un plat
          </Button>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDishDragEnd}
        >
          <SortableContext items={filteredDishes.map(d => d.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2">
              {filteredDishes.map((dish) => (
                <SortableDishItem
                  key={dish.id}
                  dish={dish}
                  onEdit={openDishModal}
                  onDelete={handleDeleteDish}
                  onDuplicate={handleDuplicateDish}
                  onToggleAvailability={handleToggleDishAvailability}
                  onUpdateStock={handleUpdateStock}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Category Modal */}
      <Dialog open={categoryModal.open} onOpenChange={(open) => !open && setCategoryModal({ open: false, data: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {categoryModal.data?.id ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label>Nom de la catégorie</Label>
            <Input
              value={categoryModal.data?.name || ''}
              onChange={(e) => setCategoryModal({ 
                ...categoryModal, 
                data: { ...categoryModal.data, name: e.target.value } 
              })}
              placeholder="Ex: Entrées, Plats, Desserts..."
              className="mt-1.5 rounded-xl"
              onKeyDown={(e) => e.key === 'Enter' && handleSaveCategory()}
            />
            
            {/* Online Only toggle for category */}
            <div className="mt-4 p-3 border border-indigo-200 rounded-xl bg-indigo-50/50">
              <div className="flex items-center gap-3">
                <Checkbox
                  id="cat-online-only"
                  checked={categoryModal.data?.online_only === true}
                  onCheckedChange={(checked) => setCategoryModal({
                    ...categoryModal,
                    data: { ...categoryModal.data, online_only: !!checked }
                  })}
                  data-testid="category-online-only-toggle"
                />
                <div>
                  <Label htmlFor="cat-online-only" className="font-semibold cursor-pointer">Paiement en ligne uniquement</Label>
                  <p className="text-xs text-gray-500 mt-0.5">Tous les plats de cette categorie ne pourront etre payes qu'en ligne</p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCategoryModal({ open: false, data: null })} disabled={saving}>
              Annuler
            </Button>
            <Button onClick={handleSaveCategory} className="bg-emerald-500 hover:bg-emerald-600" disabled={saving}>
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dish Modal with Tabs */}
      <Dialog open={dishModal.open} onOpenChange={(open) => !open && setDishModal({ open: false, data: null })}>
        <DialogContent className="max-w-4xl max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {dishModal.data?.id ? 'Modifier le plat' : 'Nouveau plat'}
            </DialogTitle>
          </DialogHeader>
          
          <Tabs defaultValue="general" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="general">Général</TabsTrigger>
              <TabsTrigger value="options">Options</TabsTrigger>
              <TabsTrigger value="allergens">Allergènes</TabsTrigger>
              <TabsTrigger value="ingredients">Ingrédients</TabsTrigger>
            </TabsList>

            {/* General Tab */}
            <TabsContent value="general" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 sm:col-span-1">
                  <Label>Nom du plat *</Label>
                  <Input
                    value={dishModal.data?.name || ''}
                    onChange={(e) => setDishModal({ 
                      ...dishModal, 
                      data: { ...dishModal.data, name: e.target.value } 
                    })}
                    placeholder="Ex: Salade César"
                    className="mt-1.5 rounded-xl"
                  />
                </div>
                
                <div className="col-span-2 sm:col-span-1">
                  <Label>Prix (€) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={dishModal.data?.price || ''}
                    onChange={(e) => setDishModal({ 
                      ...dishModal, 
                      data: { ...dishModal.data, price: e.target.value } 
                    })}
                    placeholder="0.00"
                    className="mt-1.5 rounded-xl"
                  />
                </div>

                <div className="col-span-2 sm:col-span-1">
                  <Label>TVA (%) *</Label>
                  <select
                    value={dishModal.data?.vat_rate || '10'}
                    onChange={(e) => setDishModal({ 
                      ...dishModal, 
                      data: { ...dishModal.data, vat_rate: e.target.value } 
                    })}
                    className="mt-1.5 w-full h-10 px-3 rounded-xl border border-gray-200 bg-white focus:border-emerald-500 focus:ring-emerald-500"
                  >
                    <option value="5.5">5,5% - Vente à emporter</option>
                    <option value="10">10% - Sur place (standard)</option>
                    <option value="20">20% - Boissons alcoolisées</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    5,5% = emporter | 10% = sur place | 20% = alcool
                  </p>
                </div>
              </div>
              
              <div>
                <Label>Description</Label>
                <Textarea
                  value={dishModal.data?.description || ''}
                  onChange={(e) => setDishModal({ 
                    ...dishModal, 
                    data: { ...dishModal.data, description: e.target.value } 
                  })}
                  placeholder="Description du plat..."
                  className="mt-1.5"
                  rows={3}
                />
              </div>

              {/* Image Upload */}
              <div>
                <Label>Image du plat</Label>
                <div className="mt-1.5 space-y-3">
                  {dishModal.data?.image && (
                    <div className="relative aspect-video rounded-xl overflow-hidden bg-gray-100 max-w-xs">
                      <img src={getFullImageUrl(dishModal.data.image)} alt="Preview" className="w-full h-full object-cover" />
                      <div className="absolute top-2 right-2 flex gap-1">
                        <button
                          onClick={() => handleRotateDishImage('ccw')}
                          disabled={rotatingDish}
                          className="p-1.5 bg-white/90 text-gray-700 rounded-full hover:bg-white shadow-sm"
                          title="Pivoter à gauche"
                          data-testid="rotate-dish-ccw"
                        >
                          {rotatingDish ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => handleRotateDishImage('cw')}
                          disabled={rotatingDish}
                          className="p-1.5 bg-white/90 text-gray-700 rounded-full hover:bg-white shadow-sm"
                          title="Pivoter à droite"
                          data-testid="rotate-dish-cw"
                        >
                          {rotatingDish ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCw className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => setDishModal({
                            ...dishModal,
                            data: { ...dishModal.data, image: '' }
                          })}
                          className="p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 shadow-sm"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="rounded-xl"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {uploading ? 'Upload...' : 'Uploader'}
                    </Button>
                    <Input
                      value={dishModal.data?.image || ''}
                      onChange={(e) => setDishModal({ 
                        ...dishModal, 
                        data: { ...dishModal.data, image: e.target.value } 
                      })}
                      placeholder="ou URL de l'image..."
                      className="rounded-xl flex-1"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                <Label>Disponible à la vente</Label>
                <Switch
                  checked={dishModal.data?.is_available !== false}
                  onCheckedChange={(checked) => setDishModal({ 
                    ...dishModal, 
                    data: { ...dishModal.data, is_available: checked } 
                  })}
                />
              </div>

              {/* Stock Management Section */}
              <div className="space-y-4 p-4 border border-gray-200 rounded-xl">
                <div className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-emerald-500" />
                  <Label className="font-medium">Gestion du stock</Label>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm text-gray-600">Quantité en stock</Label>
                    <Input
                      type="number"
                      min="0"
                      value={dishModal.data?.stock ?? ''}
                      onChange={(e) => setDishModal({ 
                        ...dishModal, 
                        data: { 
                          ...dishModal.data, 
                          stock: e.target.value === '' ? null : parseInt(e.target.value) 
                        } 
                      })}
                      placeholder="∞ Illimité"
                      className="mt-1.5 rounded-xl"
                    />
                    <p className="text-xs text-gray-400 mt-1">Laissez vide pour stock illimité</p>
                  </div>
                  
                  <div>
                    <Label className="text-sm text-gray-600">Seuil de stock bas</Label>
                    <Input
                      type="number"
                      min="0"
                      value={dishModal.data?.low_stock_threshold ?? ''}
                      onChange={(e) => setDishModal({ 
                        ...dishModal, 
                        data: { 
                          ...dishModal.data, 
                          low_stock_threshold: e.target.value === '' ? '' : parseInt(e.target.value) 
                        } 
                      })}
                      placeholder="5"
                      className="mt-1.5 rounded-xl"
                    />
                    <p className="text-xs text-gray-400 mt-1">Alerte si stock ≤ cette valeur</p>
                  </div>
                </div>

                {dishModal.data?.stock !== null && dishModal.data?.stock !== undefined && (
                  <div className={`p-3 rounded-lg text-sm ${
                    dishModal.data.stock === 0 
                      ? 'bg-red-50 text-red-700' 
                      : dishModal.data.stock <= (dishModal.data.low_stock_threshold || 5) 
                        ? 'bg-orange-50 text-orange-700' 
                        : 'bg-emerald-50 text-emerald-700'
                  }`}>
                    {dishModal.data.stock === 0 
                      ? '⚠️ Ce plat sera marqué comme épuisé (indisponible automatiquement)'
                      : dishModal.data.stock <= (dishModal.data.low_stock_threshold || 5)
                        ? `⚠️ Stock bas - seulement ${dishModal.data.stock} unité(s) restante(s)`
                        : `✓ Stock suffisant - ${dishModal.data.stock} unité(s) disponible(s)`
                    }
                  </div>
                )}
              </div>

              {/* Deposit (Acompte) Section */}
              <div className="space-y-4 p-4 border border-amber-200 rounded-xl bg-amber-50/30">
                <div className="flex items-center gap-2">
                  <Wallet className="w-4 h-4 text-amber-600" />
                  <Label className="font-medium text-amber-800">Acompte pour ce plat</Label>
                </div>
                
                <p className="text-sm text-gray-500">
                  Demandez un acompte spécifique pour ce plat (ex: plateaux traiteur, commandes spéciales)
                </p>
                
                <div className="flex items-center justify-between p-3 bg-white rounded-xl border border-amber-200">
                  <div>
                    <Label className="text-gray-700">Exiger un acompte</Label>
                    <p className="text-xs text-gray-500">Le client devra payer un acompte en ligne</p>
                  </div>
                  <Switch
                    checked={dishModal.data?.requires_deposit === true}
                    onCheckedChange={(checked) => setDishModal({ 
                      ...dishModal, 
                      data: { 
                        ...dishModal.data, 
                        requires_deposit: checked ? true : null,
                        deposit_type: checked ? (dishModal.data?.deposit_type || 'percentage') : null,
                        deposit_value: checked ? (dishModal.data?.deposit_value || 30) : null
                      } 
                    })}
                    data-testid="dish-requires-deposit"
                  />
                </div>

                {dishModal.data?.requires_deposit && (
                  <div className="space-y-4 p-3 bg-white rounded-xl border border-amber-200">
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setDishModal({ 
                          ...dishModal, 
                          data: { ...dishModal.data, deposit_type: 'percentage', deposit_value: 30 } 
                        })}
                        className={`p-3 rounded-lg border-2 text-left transition-all ${
                          dishModal.data?.deposit_type === 'percentage'
                            ? 'border-amber-500 bg-amber-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        data-testid="dish-deposit-type-percentage"
                      >
                        <Percent className={`w-4 h-4 mb-1 ${dishModal.data?.deposit_type === 'percentage' ? 'text-amber-600' : 'text-gray-400'}`} />
                        <p className="text-sm font-medium">Pourcentage</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setDishModal({ 
                          ...dishModal, 
                          data: { ...dishModal.data, deposit_type: 'fixed', deposit_value: 10 } 
                        })}
                        className={`p-3 rounded-lg border-2 text-left transition-all ${
                          dishModal.data?.deposit_type === 'fixed'
                            ? 'border-amber-500 bg-amber-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        data-testid="dish-deposit-type-fixed"
                      >
                        <Euro className={`w-4 h-4 mb-1 ${dishModal.data?.deposit_type === 'fixed' ? 'text-amber-600' : 'text-gray-400'}`} />
                        <p className="text-sm font-medium">Montant fixe</p>
                      </button>
                    </div>

                    <div>
                      <Label className="text-sm">
                        {dishModal.data?.deposit_type === 'percentage' ? 'Pourcentage (20-100%)' : 'Montant (EUR)'}
                      </Label>
                      {dishModal.data?.deposit_type === 'percentage' ? (
                        <div className="flex items-center gap-3 mt-2">
                          <input
                            type="range"
                            min="20"
                            max="100"
                            step="5"
                            value={dishModal.data?.deposit_value ?? ''}
                            onChange={(e) => setDishModal({ 
                              ...dishModal, 
                              data: { ...dishModal.data, deposit_value: Number(e.target.value) } 
                            })}
                            className="flex-1 accent-amber-500"
                            data-testid="dish-deposit-value-slider"
                          />
                          <span className="text-lg font-bold text-amber-600 min-w-[50px] text-right">
                            {dishModal.data?.deposit_value || 30}%
                          </span>
                        </div>
                      ) : (
                        <Input
                          type="number"
                          min="1"
                          step="0.5"
                          value={dishModal.data?.deposit_value || ''}
                          onChange={(e) => setDishModal({ 
                            ...dishModal, 
                            data: { ...dishModal.data, deposit_value: Number(e.target.value) } 
                          })}
                          placeholder="10"
                          className="mt-1.5 rounded-xl"
                          data-testid="dish-deposit-value-input"
                        />
                      )}
                    </div>

                    {/* Preview */}
                    {dishModal.data?.price && (
                      <div className="p-3 bg-amber-100 rounded-lg">
                        <p className="text-sm text-amber-800">
                          <span className="font-medium">Aperçu:</span>{' '}
                          Pour ce plat à {parseFloat(dishModal.data.price).toFixed(2)}€, l'acompte sera de{' '}
                          <span className="font-bold">
                            {dishModal.data?.deposit_type === 'percentage'
                              ? `${((dishModal.data?.deposit_value || 30) / 100 * parseFloat(dishModal.data.price)).toFixed(2)}€`
                              : `${(dishModal.data?.deposit_value || 10).toFixed(2)}€`
                            }
                          </span>
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Preorder Section - inside pricing tab after deposit */}
            <TabsContent value="pricing" className="mt-0">
              {/* Online Only */}
              <div className="mb-4 p-4 border border-indigo-200 rounded-xl bg-indigo-50/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      id="dish-online-only"
                      checked={dishModal.data?.online_only === true}
                      onCheckedChange={(checked) => setDishModal({
                        ...dishModal,
                        data: { ...dishModal.data, online_only: !!checked }
                      })}
                      data-testid="dish-online-only-toggle"
                    />
                    <div>
                      <Label htmlFor="dish-online-only" className="font-semibold cursor-pointer">Paiement en ligne uniquement</Label>
                      <p className="text-xs text-gray-500 mt-0.5">Les clients ne pourront pas payer au comptoir pour ce plat</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-4 border border-blue-200 rounded-xl bg-blue-50/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      id="dish-preorder"
                      checked={dishModal.data?.preorder_only === true}
                      onCheckedChange={(checked) => setDishModal({
                        ...dishModal,
                        data: { 
                          ...dishModal.data, 
                          preorder_only: checked,
                          preorder_delay_days: checked ? (dishModal.data?.preorder_delay_days || 1) : 0
                        }
                      })}
                      data-testid="dish-preorder-toggle"
                    />
                    <Label htmlFor="dish-preorder" className="text-sm font-medium cursor-pointer">
                      Pré-commande uniquement
                    </Label>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1 ml-7">
                  Ce plat ne sera disponible qu'en pré-commande avec un délai minimum
                </p>
                
                {dishModal.data?.preorder_only && (
                  <div className="mt-3 ml-7">
                    <Label className="text-sm">Délai minimum (jours)</Label>
                    <Input
                      type="number"
                      min="1"
                      max="30"
                      value={dishModal.data?.preorder_delay_days || 1}
                      onChange={(e) => setDishModal({
                        ...dishModal,
                        data: { ...dishModal.data, preorder_delay_days: Math.max(1, parseInt(e.target.value) || 1) }
                      })}
                      className="mt-1.5 w-24 rounded-xl"
                      data-testid="dish-preorder-delay"
                    />
                    <p className="text-xs text-blue-600 mt-1">
                      Le client devra commander au minimum {dishModal.data?.preorder_delay_days || 1} jour(s) à l'avance
                    </p>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Options Tab - Different option types */}
            <TabsContent value="options" className="mt-4">
              <div className="space-y-4">
                <p className="text-sm text-gray-500">
                  Ajoutez des options personnalisables pour ce plat (tailles, quantités, préférences...)
                </p>

                {/* Current options list */}
                <div className="space-y-3">
                  {(dishModal.data?.options || []).map((option, optIndex) => (
                    <div key={option.id} className="border border-gray-200 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{option.name}</span>
                          <Badge variant="outline" className="text-xs">
                            {option.option_type === 'grams' ? 'Grammes' :
                             option.option_type === 'liters' ? 'Litres' :
                             option.option_type === 'text_input' ? 'Texte libre' : 'Cases à cocher'}
                          </Badge>
                          {option.required && <Badge className="bg-red-100 text-red-700 text-xs">Obligatoire</Badge>}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const newOptions = [...(dishModal.data?.options || [])];
                            newOptions.splice(optIndex, 1);
                            setDishModal({ ...dishModal, data: { ...dishModal.data, options: newOptions } });
                          }}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                      
                      {/* Show choices for checkbox/grams/liters */}
                      {option.option_type !== 'text_input' && option.choices?.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {option.choices.map(choice => (
                            <span key={choice.id} className="px-2 py-1 bg-gray-100 rounded text-sm">
                              {choice.name} {choice.price > 0 && `(+${choice.price}€)`}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      {/* Show placeholder for text_input */}
                      {option.option_type === 'text_input' && option.placeholder && (
                        <p className="text-sm text-gray-500">Placeholder: "{option.placeholder}"</p>
                      )}
                    </div>
                  ))}
                </div>

                {/* Add new option */}
                <div className="border-2 border-dashed border-gray-200 rounded-xl p-4">
                  <h4 className="font-medium text-gray-900 mb-3">Ajouter une option</h4>
                  
                  <div className="grid gap-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Nom de l'option</Label>
                        <Input
                          id="new-option-name"
                          placeholder="Ex: Taille, Quantité, Prénom..."
                          className="mt-1 rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Type d'option</Label>
                        <select
                          id="new-option-type"
                          className="w-full mt-1 rounded-lg border border-gray-200 px-3 py-2 text-sm"
                          defaultValue="checkbox"
                        >
                          <option value="checkbox">Cases à cocher</option>
                          <option value="grams">Liste en grammes</option>
                          <option value="liters">Liste en litres</option>
                          <option value="text_input">Champ à remplir</option>
                        </select>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" id="new-option-required" className="rounded" />
                        <span>Obligatoire</span>
                      </label>
                      <label className="flex items-center gap-2 text-sm" id="multiple-container">
                        <input type="checkbox" id="new-option-multiple" className="rounded" />
                        <span>Sélection multiple</span>
                      </label>
                    </div>

                    {/* Placeholder for text_input */}
                    <div id="text-input-options" className="hidden">
                      <Label className="text-xs">Placeholder (texte d'exemple)</Label>
                      <Input
                        id="new-option-placeholder"
                        placeholder="Ex: Entrez votre prénom..."
                        className="mt-1 rounded-lg text-sm"
                      />
                    </div>

                    {/* Choices for checkbox/grams/liters */}
                    <div id="choices-container">
                      <Label className="text-xs">Choix disponibles (un par ligne: nom,prix)</Label>
                      <Textarea
                        id="new-option-choices"
                        placeholder="100g,0&#10;200g,2&#10;500g,5"
                        className="mt-1 rounded-lg text-sm min-h-[80px]"
                      />
                      <p className="text-xs text-gray-400 mt-1">Format: nom,prix (ex: "Grande,2" ou "100g,0")</p>
                    </div>

                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      onClick={() => {
                        const name = document.getElementById('new-option-name')?.value?.trim();
                        const type = document.getElementById('new-option-type')?.value || 'checkbox';
                        const required = document.getElementById('new-option-required')?.checked || false;
                        const multiple = document.getElementById('new-option-multiple')?.checked || false;
                        const placeholder = document.getElementById('new-option-placeholder')?.value?.trim() || '';
                        const choicesText = document.getElementById('new-option-choices')?.value?.trim() || '';

                        if (!name) {
                          toast.error('Veuillez entrer un nom pour l\'option');
                          return;
                        }

                        let choices = [];
                        if (type !== 'text_input' && choicesText) {
                          choices = choicesText.split('\n').filter(l => l.trim()).map((line, idx) => {
                            const [choiceName, priceStr] = line.split(',').map(s => s.trim());
                            return {
                              id: `choice-${Date.now()}-${idx}`,
                              name: choiceName || line.trim(),
                              price: parseFloat(priceStr) || 0
                            };
                          });
                        }

                        if (type !== 'text_input' && choices.length === 0) {
                          toast.error('Veuillez ajouter au moins un choix');
                          return;
                        }

                        const newOption = {
                          id: `opt-${Date.now()}`,
                          name,
                          option_type: type,
                          required,
                          multiple: type !== 'text_input' ? multiple : false,
                          choices,
                          placeholder: type === 'text_input' ? placeholder : null,
                          max_length: 100
                        };

                        const currentOptions = dishModal.data?.options || [];
                        setDishModal({
                          ...dishModal,
                          data: { ...dishModal.data, options: [...currentOptions, newOption] }
                        });

                        // Reset form
                        document.getElementById('new-option-name').value = '';
                        document.getElementById('new-option-choices').value = '';
                        document.getElementById('new-option-placeholder').value = '';
                        document.getElementById('new-option-required').checked = false;
                        document.getElementById('new-option-multiple').checked = false;
                        
                        toast.success('Option ajoutée');
                      }}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Ajouter cette option
                    </Button>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Allergens Tab */}
            <TabsContent value="allergens" className="mt-4">
              <div className="space-y-4">
                <p className="text-sm text-gray-500">
                  <AlertTriangle className="w-4 h-4 inline mr-1 text-orange-500" />
                  Sélectionnez les allergènes présents dans ce plat (14 allergènes réglementaires)
                </p>
                
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {(allergensList || []).map((allergen) => {
                    const isSelected = dishModal.data?.allergens?.includes(allergen);
                    return (
                      <label
                        key={allergen}
                        className={`flex items-center gap-2 p-3 rounded-xl cursor-pointer transition-colors border ${
                          isSelected 
                            ? 'bg-orange-50 border-orange-200' 
                            : 'hover:bg-gray-50 border-gray-200'
                        }`}
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleAllergen(allergen)}
                        />
                        <span className="text-sm">{allergen}</span>
                      </label>
                    );
                  })}
                </div>

                {dishModal.data?.allergens?.length > 0 && (
                  <div className="p-3 bg-orange-50 rounded-xl">
                    <p className="text-sm font-medium text-orange-800">
                      Allergènes sélectionnés: {dishModal.data.allergens.join(', ')}
                    </p>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Ingredients Tab */}
            <TabsContent value="ingredients" className="mt-4">
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-xl">
                  <div>
                    <Label className="text-emerald-800">Activer la personnalisation</Label>
                    <p className="text-xs text-emerald-600 mt-1">
                      Idéal pour les bars à salade, poke bowls, etc.
                    </p>
                  </div>
                  <Switch
                    checked={dishModal.data?.ingredients?.enabled || false}
                    onCheckedChange={(checked) => setDishModal({
                      ...dishModal,
                      data: {
                        ...dishModal.data,
                        ingredients: {
                          enabled: checked,
                          categories: dishModal.data?.ingredients?.categories || []
                        }
                      }
                    })}
                  />
                </div>

                {dishModal.data?.ingredients?.enabled && (
                  <>
                    {/* Add Category Button */}
                    <div className="flex items-center justify-between">
                      <Label>Catégories d&apos;ingrédients</Label>
                      <Button size="sm" variant="outline" onClick={addIngredientCategory} className="rounded-full">
                        <Plus className="w-4 h-4 mr-1" />
                        Nouvelle catégorie
                      </Button>
                    </div>

                    {(!dishModal.data?.ingredients?.categories || dishModal.data.ingredients.categories.length === 0) ? (
                      <div className="text-center py-8 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                        <Leaf className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">
                          Aucune catégorie. Créez des catégories comme &quot;Base&quot;, &quot;Légumes&quot;, &quot;Protéines&quot;...
                        </p>
                      </div>
                    ) : (
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleIngredientCategoryDragEnd}
                      >
                        <SortableContext 
                          items={(dishModal.data?.ingredients?.categories || []).map(c => c.id)} 
                          strategy={verticalListSortingStrategy}
                        >
                          <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
                            {(dishModal.data?.ingredients?.categories || []).map((category, catIndex) => (
                              <SortableIngredientCategory
                                key={category.id}
                                category={category}
                                catIndex={catIndex}
                                onUpdate={updateIngredientCategory}
                                onRemove={removeIngredientCategory}
                                onAddItem={addIngredientItem}
                                onUpdateItem={updateIngredientItem}
                                onRemoveItem={removeIngredientItem}
                                onReorderItems={reorderIngredientItems}
                              />
                            ))}
                          </div>
                        </SortableContext>
                      </DndContext>
                    )}

                    {/* Summary */}
                    {dishModal.data?.ingredients?.categories?.length > 0 && (
                      <div className="p-3 bg-emerald-50 rounded-xl text-sm">
                        <p className="font-medium text-emerald-800">Résumé:</p>
                        <ul className="mt-1 space-y-1 text-emerald-700">
                          <li>• {dishModal.data?.ingredients?.categories?.length || 0} catégorie(s)</li>
                          <li>• {(dishModal.data?.ingredients?.categories || []).reduce((sum, c) => sum + (c.items?.length || 0), 0)} ingrédient(s) au total</li>
                          <li>• {(dishModal.data?.ingredients?.categories || []).filter(c => c.min_selection > 0).length} catégorie(s) obligatoire(s)</li>
                        </ul>
                      </div>
                    )}
                  </>
                )}
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDishModal({ open: false, data: null })} disabled={saving}>
              Annuler
            </Button>
            <Button onClick={handleSaveDish} className="bg-emerald-500 hover:bg-emerald-600" disabled={saving}>
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PIN Modal - Set/Edit PIN */}
      <SetPinModal
        open={pinModal.open}
        onOpenChange={(open) => setPinModal({ ...pinModal, open })}
        onConfirm={handleSavePin}
        currentHasPin={pinModal.category?.has_pin}
        categoryName={pinModal.category?.name}
      />

      {/* PIN Modal - Verify PIN */}
      <VerifyPinModal
        open={verifyPinModal.open}
        onOpenChange={(open) => setVerifyPinModal({ ...verifyPinModal, open })}
        onVerify={handleVerifyPin}
        categoryName={verifyPinModal.category?.name}
      />

      {/* Delete Dish Confirmation Dialog */}
      <AlertDialog open={deleteConfirm.open} onOpenChange={(open) => !open && setDeleteConfirm({ open: false, id: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce plat ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Le plat sera définitivement supprimé de votre menu.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteDish} className="bg-red-500 hover:bg-red-600">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Category Confirmation Dialog */}
      <AlertDialog open={deleteCatConfirm.open} onOpenChange={(open) => !open && setDeleteCatConfirm({ open: false, id: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette catégorie ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. La catégorie et tous ses plats seront définitivement supprimés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteCategory} className="bg-red-500 hover:bg-red-600">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
